import { Fragment, forwardRef, useRef, useState } from "react";

import { useMostRecentPublishedModelsQuery } from "@/apollo/types";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import { formatModelName, useModelSearch } from "@/features/models";
import cn from "@/helpers/classNames";
import { useMountEffect } from "@/hooks/useMountEffect";
import { IntegrationLogoBox } from "@/integrations";
import { useDataWarehouseContext } from "@/providers/DataWarehouseProvider";
import { Combobox, ComboboxProps, Transition } from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/20/solid";

import { SourceModel } from "../../form";

type ModelSelectorProps = ComboboxProps<SourceModel, true, false, "div">;

export const ModelSelector = forwardRef<
  HTMLDivElement,
  ModelSelectorProps & {
    defaultOpen?: boolean;
  }
>(({ defaultOpen, ...props }, ref) => {
  const [query, setQuery] = useState("");
  const { models: searchResults, isSearching } = useModelSearch(query, {
    publishedOnly: true,
  });
  const dwh = useDataWarehouseContext();
  const { data: recentlyPublishedModelsData } =
    useMostRecentPublishedModelsQuery();

  const inputRef = useRef<HTMLInputElement>(null);

  const forceOpenDropdown = () => {
    // The Combobox does not have a programmatic way to open the dropdown
    // We can force it by dispatching a keyboard event (alternatively, by simulating a click on the dropdown button)
    if (inputRef.current) {
      inputRef.current.focus();
      const event = new KeyboardEvent("keydown", {
        key: "ArrowDown",
        code: "ArrowDown",
        keyCode: 40,
        which: 40,
        bubbles: true,
      });
      inputRef.current.dispatchEvent(event);
    }
  };

  useMountEffect(() => {
    setTimeout(() => {
      if (defaultOpen) {
        forceOpenDropdown();
      }
    }, 400);
  });

  return (
    <Combobox<SourceModel, "div">
      as="div"
      ref={ref}
      {...props}
      onClick={() => forceOpenDropdown()}
    >
      <div className="relative">
        <div className="relative flex h-11 w-full cursor-default items-center overflow-hidden rounded-sm border text-left text-sm focus-within:outline focus-within:outline-blue-600">
          <IntegrationLogoBox
            id={dwh.integration.id}
            size="xs"
            className="ml-2 h-8 w-8"
          />
          <Combobox.Input
            className="h-full w-full truncate border-none bg-transparent py-2 pl-2 pr-10 text-sm leading-5 text-foreground focus:outline-none focus:ring-0"
            displayValue={(model: NonNullable<typeof searchResults>[0]) =>
              model ? formatModelName(model) : ""
            }
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            placeholder="Search for a model..."
            ref={inputRef}
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon
              className="h-5 w-5 text-muted-foreground"
              aria-hidden="true"
            />
          </Combobox.Button>
        </div>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setQuery("")}
        >
          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-sm bg-background text-base text-foreground shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-gray-900">
            <div className="space-y-1 py-1 text-sm">
              {query.trim().length === 0 ? (
                <div className="relative cursor-default select-none px-4 py-2 text-muted-foreground">
                  <em>Start typing to search for a model.</em>
                </div>
              ) : isSearching ? (
                <div className="relative flex cursor-default select-none items-center px-4 py-2 text-muted-foreground">
                  <LoadingSpinner className="mr-3 h-4 w-4" />
                  Searching...
                </div>
              ) : searchResults.length === 0 ? (
                <div className="relative cursor-default select-none px-4 py-2 text-muted-foreground">
                  No models found.
                </div>
              ) : null}
              <div>
                {searchResults.map((model) => {
                  const label = formatModelName(model);
                  return (
                    <Combobox.Option
                      key={model.id}
                      value={model}
                      title={label}
                      className={({ active }) =>
                        cn(
                          `relative cursor-default select-none truncate px-4 py-2`,
                          {
                            "bg-blue-500 text-white": active,
                          },
                        )
                      }
                    >
                      {label}
                    </Combobox.Option>
                  );
                })}
              </div>
              {recentlyPublishedModelsData?.mostRecentPublishedModels.length ? (
                <div>
                  <div className="cursor-default select-none border-t px-4 py-3 text-xs font-medium uppercase text-muted-foreground">
                    Recently Published Models
                  </div>
                  {recentlyPublishedModelsData.mostRecentPublishedModels.map(
                    (model) => {
                      const label = formatModelName(model);
                      return (
                        <Combobox.Option
                          key={model.id}
                          value={model}
                          title={label}
                          className={({ active }) =>
                            cn(
                              `relative cursor-default select-none truncate px-4 py-2`,
                              {
                                "bg-blue-500 text-white": active,
                              },
                            )
                          }
                        >
                          {label}
                        </Combobox.Option>
                      );
                    },
                  )}
                </div>
              ) : null}
            </div>
          </Combobox.Options>
        </Transition>
      </div>
    </Combobox>
  );
});
